/*
 * The client half of the Optimus approved-campaign contract.
 *
 * The server resolves ONE typed answer per campaign — its approved flight and
 * its approved planner platform selection — and this module is the only place
 * the page reads it. Optimus previously derived those two answers separately,
 * from advertiser-wide inputs, in two different files; that is how one
 * advertiser's two overlapping approved campaigns came to share one platform
 * set and one unresolved window.
 *
 * Nothing here re-derives the contract. It parses, it presents, and it refuses.
 */

import { providerSurfaceLabel } from "./providerSurfaceRegistry";
import { expandOptimizerPlatformIds } from "./optimusPlatformTabs";

export const OPTIMUS_CONTRACT_STATE = Object.freeze({
  IDLE: "idle",
  LOADING: "loading",
  RESOLVED: "resolved",
  UNRESOLVED: "unresolved",
});

export const OPTIMUS_SELECTION_MODE = Object.freeze({
  APPROVED_FLIGHT: "approved_flight",
  EXPLICIT_RANGE: "explicit_range",
});

const asDateOnly = (value) => {
  const match = String(value ?? "").trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[1]}-${match[2]}-${match[3]}` : null;
};

/*
 * Calendar dates stay calendar dates.
 *
 * `startMs`/`endMs` exist only for same-page comparisons and are built from
 * LOCAL midnight, matching the rest of the Optimizer's date handling. The
 * `start`/`end` strings — the values that travel to the API, into the run
 * record and into every label — are never round-tripped through an instant, so
 * no viewer's time zone can move an approved flight by a day.
 */
export const buildOptimusContractFlightRange = (contract) => {
  const start = asDateOnly(contract?.flight?.startDate);
  const end = asDateOnly(contract?.flight?.endDate);
  if (!start || !end) return null;

  const toMs = (value) => {
    const [year, month, day] = value.split("-").map(Number);
    return new Date(year, month - 1, day).getTime();
  };

  return { start, end, startMs: toMs(start), endMs: toMs(end) };
};

/*
 * The campaign's platform tabs, in the order the contract already decided.
 *
 * The server ranks by approved allocation; that order is preserved verbatim
 * here rather than re-sorted, so the tab strip reads as the plan reads. "All
 * Platforms" leads because it is a scope, not a channel.
 */
export const resolveOptimusContractPlatformTabs = (contract, labelForPlatform = null) => {
  const platforms = Array.isArray(contract?.platforms) ? contract.platforms : [];
  if (!platforms.length) return [];

  const tabs = [];
  const seen = new Set();

  platforms.forEach((platform) => {
    const ids = expandOptimizerPlatformIds(platform?.key);
    ids.forEach((id) => {
      if (!id || seen.has(id)) return;
      seen.add(id);
      const label =
        ids.length === 1 && platform?.label
          ? platform.label
          : typeof labelForPlatform === "function"
          ? labelForPlatform(id)
          : id;
      tabs.push({ id, label: providerSurfaceLabel(id) || label || id, plannerKey: platform?.key || null });
    });
  });

  if (!tabs.length) return [];
  return [
    {
      id: "all",
      label: typeof labelForPlatform === "function" ? labelForPlatform("all") : "All Platforms",
      plannerKey: null,
    },
    ...tabs,
  ];
};

export const resolveOptimusContractPlatformIds = (contract) =>
  resolveOptimusContractPlatformTabs(contract)
    .map((tab) => tab.id)
    .filter((id) => id && id !== "all");

/*
 * The blocking message.
 *
 * The old copy said the same sentence for every cause: a campaign that was
 * never approved, a campaign belonging to another advertiser, and a campaign
 * whose approved plan genuinely carries no dates all read identically, so the
 * message could not be acted on and could not be trusted. Each cause now names
 * itself, and every message still ends with the same governed promise: nothing
 * else was evaluated in its place.
 */
export const describeOptimusContractDiagnostic = (diagnostic) => {
  if (!diagnostic || typeof diagnostic !== "object") return "";

  const campaign = diagnostic.campaignName
    ? `“${diagnostic.campaignName}”`
    : diagnostic.campaignId
    ? `campaign ${diagnostic.campaignId}`
    : "this campaign";
  const held =
    " Optimizations are held rather than run against a different window. Choose 30D / 14D / 7D to run an explicit range.";

  switch (diagnostic.code) {
    case "APPROVED_FLIGHT_MISSING":
      return `The approved plan for ${campaign} carries no flight start and end (expected ${diagnostic.expectedField}).${held}`;
    case "APPROVED_FLIGHT_INVALID":
      return `The approved flight recorded for ${campaign} is not a usable calendar range: ${diagnostic.reason}${held}`;
    case "APPROVED_PLATFORMS_MISSING":
      return `The approved plan for ${campaign} names no platform selection, so there is nothing to optimize against.${held}`;
    case "CAMPAIGN_NOT_APPROVED":
      return `${campaign} is not an approved plan (${diagnostic.reason}), so it has no approved flight or platform selection.${held}`;
    case "CAMPAIGN_ADVERTISER_MISMATCH":
      return `${campaign} belongs to a different advertiser and was not applied. Choose a campaign from this advertiser.`;
    case "CAMPAIGN_NOT_FOUND":
      return `No approved campaign was found for ${campaign}.${held}`;
    default:
      return `The approved campaign contract for ${campaign} could not be resolved (${
        diagnostic.code || "unknown"
      }).${held}`;
  }
};

/*
 * The identity a run submits with itself.
 *
 * The server re-resolves the contract and compares. If the approved artifact
 * moved — or the operator moved to another campaign — between hydration and
 * submit, the run is refused rather than quietly evaluated against whichever
 * shape happened to win the race.
 */
export const buildOptimusRunContractPayload = ({
  contract = null,
  selectionMode = OPTIMUS_SELECTION_MODE.APPROVED_FLIGHT,
  platformIds = null,
} = {}) => {
  if (!contract) return { selectionMode: OPTIMUS_SELECTION_MODE.EXPLICIT_RANGE };

  return {
    selectionMode,
    approvalIdentity: contract.approvalIdentity || null,
    approvedFlight:
      selectionMode === OPTIMUS_SELECTION_MODE.APPROVED_FLIGHT && contract.flight
        ? { startDate: contract.flight.startDate, endDate: contract.flight.endDate }
        : null,
    platformIds: Array.isArray(platformIds)
      ? platformIds
      : resolveOptimusContractPlatformIds(contract),
  };
};

/*
 * Does this contract describe the campaign the page is currently addressing?
 *
 * A contract is fetched asynchronously; a campaign switch is instant. Without
 * this check the previous campaign's flight and tabs stay on screen for the
 * duration of one request — which is precisely a leak between the two Never
 * Stop Chasing campaigns, just a short-lived one.
 */
export const contractMatchesSelection = (contract, { advertiserId, campaignId } = {}) => {
  if (!contract) return false;
  const sameCampaign = String(contract.campaignId ?? "") === String(campaignId ?? "");
  const sameAdvertiser =
    contract.advertiserId == null || String(contract.advertiserId) === String(advertiserId ?? "");
  return Boolean(campaignId) && sameCampaign && sameAdvertiser;
};
